import React, { useState } from "react";
import {
  ANY,
  DEFAULT_FORM_ROW_GUTTER,
  EFFECTIVE,
  LABEL,
  SectionKeys,
  Selects,
} from "../../utils/Constants";
import {
  checkIsSecondaryItem,
  parseJSONValueFromAutocomplete,
  prepareSuggestionsWithCustomLabels,
  transformAutocompleteAgency,
  transformLocationsAutocompleteData,
} from "../../utils/MiscUtils";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import Form from "antd/lib/form/Form";
import CardBlock from "../../components/CardBlock/CardBlock";
import FormItem from "antd/lib/form/FormItem";
import FormItemLabel from "antd/lib/form/FormItemLabel";
import Checkbox from "../../components/Checkbox/Checkbox";
import Autosuggest from "../../components/Autosuggest/Autosuggest";
import {
  fetchAgencies,
  fetchAirlines,
  fetchStationLocations,
  fetchWrapper,
  SHOW_NON_EFFECTIVE_FALSE,
  SHOW_NON_EFFECTIVE_TRUE_EXTRA_PARAM,
} from "../../utils/ApiUtils";
import { IAutoCompleteChangeHandlerProps } from "../../views/SearchesManager/SearchManagerForm/SearchManagerForm";
import { IAutocompleteObject } from "../../store/state/SearchManager/FormData";
import SMFormSelect from "../../views/SearchesManager/SearchManagerForm/SMFormSelect/SMFormSelect";
import Select from "antd/lib/select";
import FormInput from "../../components/FormInput/FormInput";
import { transformAutocompleteData } from "../../utils/MiscUtils";
import { IItem, IRow } from "../EditorLayout/EditorLayout";
import { Configuration } from "./FiltersTableLayoutsWrapper";
import { FiltersData } from "./FiltersTableLayoutsWrapper";
import styles from "../../views/SearchesManager/SearchManagerRule/RuleInfo/RuleInfo.module.css";
import { BrandAndMarketListItem } from "../../integrated-client";
import TableFormatter from "../../utils/TableFormatter";

interface IFiltersLayoutProps {
  handleFiltersDataUpdate: (value: any) => void; // to be replaced to Utils
  filtersData: FiltersData;
  configuration: Configuration | null;
  clearValues: Array<keyof FiltersData> | null;
  clearAgency: boolean | false;
  setClearValues: (value: Array<keyof FiltersData> | null) => void;
  resetClearValues: () => void;
  setCurrentChangedId: (id: string) => void;
  rows: Array<IRow>;
  title?: string;
  renderButtons: () => JSX.Element;
  sectionKey?: SectionKeys;
}

interface IHandleBlur {
  id: keyof FiltersData;
  value: string | boolean | number | IAutocompleteObject | null;
}

const FiltersLayout = (props: IFiltersLayoutProps) => {
  const {
    handleFiltersDataUpdate,
    filtersData,
    configuration,
    clearValues,
    setClearValues,
    resetClearValues,
    setCurrentChangedId,
    rows,
    title,
    renderButtons,
    sectionKey,
  } = props;

  const [clearAgency, setClearAgency] = useState(props.clearAgency || false);

  const handleAutoSuggestBlur = ({ id, value }: IHandleBlur) => {
    const storeValue: any = id ? filtersData[id] : null;
    if (storeValue === null) {
      setClearValues([id]);
      return true;
    }
    //to be move on function in miscUtils (`(${storeValue?.code}) ${storeValue?.label}`)
    if (getDisplayText({ id } as IItem, storeValue) !== value) {
      handleFiltersDataUpdate({ [id]: null });
      setClearValues([id]);
      return true;
    }
    return false;
  };

  const resetClearagency = () => {
    setClearAgency(false);
  };

  const isElementDisabled = (item: IItem) =>
    item.dependsOn?.some((id: string) => !(filtersData as any)[id]);

  const handleAutocompleteChange = (props: IAutoCompleteChangeHandlerProps) => {
    const { value, option, formField } = props;
    if (option?.value) {
      let parsedVal = parseJSONValueFromAutocomplete(option.value);
      const newValObj: any = {
        [formField]: {
          id: parsedVal.id || undefined,
          label: parsedVal.label,
          code: parsedVal.code || parsedVal.value,
          isLocation:
            parsedVal.isLocation === true || parsedVal.isLocation === false
              ? parsedVal.isLocation
              : undefined,
          effective:
            parsedVal.effective === true || parsedVal.effective === false
              ? parsedVal.effective
              : undefined,
        },
      };
      if (formField === "agency") {
        newValObj[formField].id = parsedVal.id;
        newValObj[formField].code = parsedVal.code;
      }
      handleFiltersDataUpdate(newValObj);
    } else if (value === undefined && option === undefined && formField) {
      handleFiltersDataUpdate({ [formField]: null });
    }
    setCurrentChangedId(formField);
    return value;
  };

  const handleSelect = (value: string, option: any, fieldName: string) => {
    let newVal, formDataBNM, valueBNM;
    if (props.filtersData !== null) {
      formDataBNM = (filtersData as any)?.brandAndMarket;
    }
    try {
      newVal = JSON.parse(value);
      if (fieldName === "brandAndMarket") {
        formDataBNM = `${formDataBNM?.brand?.label} (${formDataBNM?.market?.label})`;
        valueBNM = `${newVal?.brand?.label} (${newVal?.market?.label})`;
        if (
          ((props.filtersData as any)?.brandAndMarket !== null ||
            newVal !== null) &&
          formDataBNM !== valueBNM
        ) {
          setClearAgency(true);
          handleFiltersDataUpdate({ agency: null });
        }
      }
    } catch {
      newVal = null;
    }
    setCurrentChangedId(fieldName);
    handleFiltersDataUpdate({ [fieldName]: newVal });
  };

  const getDisplayText = (item: IItem, value: any) => {
    if (!item || !value) return null;
    let displayText;
    displayTextRenderMethods.forEach(
      (arrayOfId: Array<string>, renderFunc: any) => {
        if (arrayOfId.includes(item.id)) displayText = renderFunc(value, item);
      }
    );
    return displayText || renderDefaultDisplayText(value);
  };

  const renderDefaultDisplayText = (value: any) =>
    value.label ? value.label : value;
  const renderWithCodeDisplayText = (value: any) =>
    `(${value.code}) ${value.label}`;
  const renderBrandAndMarketDisplayText = (value: BrandAndMarketListItem) =>
    value ? TableFormatter.formatBrandAndMarketObject(value) : null;
  const renderCheckboxDisplayText = (value: any, item: IItem) => {
    return value ? item.label : null;
  };

  const displayTextRenderMethods = new Map([
    [
      renderWithCodeDisplayText,
      ["agency", "origin", "destination", "oneAirLocation"],
    ],
    [renderDefaultDisplayText, ["default"]],
    [renderBrandAndMarketDisplayText, ["brandAndMarket"]],
    [renderCheckboxDisplayText as any, ["showDeleted", "showDisabled"]],
  ]);

  const renderInput = (item: IItem) => {
    const {
      id,
      label,
      inputType = "text",
      span = "6",
      isTrimRequired = false,
    } = item;

    return (
      <Col span={span}>
        <FormInput
          id={id}
          labelText={label}
          type={inputType}
          value={filtersData[id] || ""}
          onChange={(e: any) => {
            setCurrentChangedId(id);
            handleFiltersDataUpdate({ [id]: e.target.value || null });
          }}
          onBlur={(e: any) =>
            isTrimRequired &&
            handleFiltersDataUpdate({ [id]: e.target.value.trim() || null })
          }
        />
      </Col>
    );
  };

  const renderSelect = (item: IItem) => {
    const {
      id,
      label,
      span = "6",
      disabled = false,
      allowClear = false,
      configurationProperty,
    } = item;
    return (
      <Col span={span} id={`select-${id}`}>
        <SMFormSelect
          id={id}
          labelText={label}
          allowClear={allowClear && !!filtersData[id]}
          value={filtersData[id] ? getDisplayText(item, filtersData[id]) : ANY} //to be typed
          disabled={disabled || (item.dependsOn && isElementDisabled(item))}
          hasSecondaryValueSelected={
            (filtersData[id] as any)?.effective === false
          } //to be typed
          onChange={(value: string, option: any) =>
            handleSelect(value, option, id)
          }
          options={() => (
            <>
              <Select.Option className={styles.customSelectOption} value={ANY}>
                {ANY}
              </Select.Option>
              {configurationProperty &&
                configuration &&
                (configuration as any)[configurationProperty]?.map(
                  (el: any) => {
                    if (el?.effective === false) return null;
                    return (
                      <Select.Option
                        className={styles.customSelectOption}
                        value={JSON.stringify(el)}
                      >
                        {getDisplayText(item, el)}
                      </Select.Option>
                    );
                  }
                )}
            </>
          )}
          wrapperClassName={
            id === "ancillaryCategory" ? `form-input-${id}` : ""
          }
        />
      </Col>
    );
  };

  const renderAutoSuggest = (item: IItem) => {
    const { id, label, span = "6", allowClear = false } = item;
    return (
      <Col span={span}>
        <FormItem>
          <FormItemLabel prefixCls={id} htmlFor={id} label={label} />
          <Autosuggest
            startValue={
              filtersData[id]
                ? getDisplayText(item, filtersData[id])
                : undefined
            }
            id={id}
            disabled={
              id === "agency" &&
              filtersData &&
              (filtersData as any)?.brandAndMarket === undefined
                ? true
                : false
            }
            hasSecondaryValueSelected={checkIsSecondaryItem(
              filtersData[id],
              EFFECTIVE
            )}
            clearValue={
              id === "agency" && clearAgency === true
                ? clearAgency
                : clearValues?.includes(id)
            }
            clearedValueCallback={
              id === "agency" && clearAgency === true
                ? resetClearagency
                : resetClearValues
            }
            allowClear={allowClear && !!filtersData[id]}
            useCustomOptions={true}
            customOptionLabelField={LABEL}
            fetchFunction={fetchWrapper(
              (fetchObject[id] as any).fetchFunc,
              (fetchObject[id] as any).params
            )}
            onBlurHandler={handleAutoSuggestBlur}
            resultDataTransformer={(fetchObject[id] as any)?.transformDataFunc}
            renderOptionsHandler={prepareSuggestionsWithCustomLabels(EFFECTIVE)}
            changeHandler={(value: string, option: any) =>
              handleAutocompleteChange({
                value,
                option,
                formField: id,
              })
            }
          />
        </FormItem>
      </Col>
    );
  };

  const renderCheckbox = (item: IItem) => {
    const { id, label, span = "6" } = item;

    return (
      <Col span={span}>
        <FormItem className={styles.boolWrapper}>
          <FormItemLabel prefixCls={id} htmlFor={id} label={label} />
          <Checkbox
            id={id}
            checked={filtersData[id]}
            onChange={() => handleFiltersDataUpdate({ [id]: !filtersData[id] })}
          />
        </FormItem>
      </Col>
    );
  };

  const fetchObject = {
    agency: {
      fetchFunc: fetchAgencies,
      transformDataFunc: transformAutocompleteAgency,
      params: {
        ...SHOW_NON_EFFECTIVE_FALSE,
        brandsAndMarkets: !Array.isArray((filtersData as any)?.brandAndMarket)
          ? [(filtersData as any)?.brandAndMarket]
          : (filtersData as any)?.brandAndMarket,
        strictBrandAndMarket: true,
      },
    },
    carrier: {
      fetchFunc: fetchAirlines,
      transformDataFunc: transformLocationsAutocompleteData,
      params: {
        ...SHOW_NON_EFFECTIVE_TRUE_EXTRA_PARAM,
      },
    },
    airline: {
      fetchFunc: fetchAirlines,
      transformDataFunc: transformLocationsAutocompleteData,
      params: {
        ...SHOW_NON_EFFECTIVE_TRUE_EXTRA_PARAM,
      },
    },
    oneAirLocation: {
      fetchFunc: fetchStationLocations,
      transformDataFunc: transformAutocompleteData,
      params: {
        ...SHOW_NON_EFFECTIVE_TRUE_EXTRA_PARAM,
      },
    },
  };

  type renderFunc = (item: IItem) => JSX.Element;

  interface IElements {
    select: renderFunc;
    autoSuggest: renderFunc;
    input: renderFunc;
    checkbox: renderFunc;
  }

  const elements: IElements = {
    select: renderSelect,
    autoSuggest: renderAutoSuggest,
    input: renderInput,
    checkbox: renderCheckbox,
  };

  return (
    <>
      <Form>
        <CardBlock title={title}>
          {rows &&
            rows.map((row: IRow) => (
              <Row gutter={DEFAULT_FORM_ROW_GUTTER}>
                {row.items.map((item: IItem) =>
                  (elements as any)[item.type](item)
                )}
              </Row>
            ))}
          {sectionKey === "ancillariesManager" ? <br/>: null}
          {renderButtons()}
        </CardBlock>
      </Form>
    </>
  );
};

export default FiltersLayout;
